<template>
  <v-sheet
    class="top-bar d-flex align-center border-b-sm px-10"
    height="58px"
  >
    <div class="w-25 d-flex align-center">
      <HideRbac :permissions="['finalizeProfileTemplate']">
        <v-btn
          v-if="isTemplate"
          class="publish-btn text-button-2 rounded-pill px-2 ga-1 flex-shrink-0"
          variant="flat"
          height="28"
          :disabled="publishDisabled"
          @click="showModal = true"
        >
          <DpplSvg
            file-name="icons/Icon_Publish"
            class="flex-shrink-0"
            aria-hidden="true"
          />
          Publish
        </v-btn>
      </HideRbac>
      <HideRbac :permissions="['publishDraftProfile']">
        <v-btn
          v-if="isStandaloneJobNav && !isTemplate && !isHrisProfile"
          class="publish-btn text-button-2 rounded-pill px-2 ga-1 flex-shrink-0"
          variant="flat"
          height="28"
          :disabled="draftPublishDisabled"
          @click="showDraftPublishModal = true"
        >
          <DpplSvg
            file-name="icons/Icon_Publish"
            class="flex-shrink-0"
            aria-hidden="true"
          />
          Publish
        </v-btn>
      </HideRbac>
    </div>
    <div class="w-50 d-flex align-center ga-3">
      <template v-if="hasEditAccess || checkRbacPermissions(['applyProfileTemplate'])">
        <v-btn
          v-if="!isTemplate && !isHrisProfile"
          class="ghost-btn text-button-2 text-textDark"
          variant="text"
          rounded="lg"
          height="40"
          :disabled="applyTemplateDisabled"
          @click="setModal({ field: 'applyJobProfileTemplate', value: true })"
        >
          Apply Template
        </v-btn>
      </template>
      <v-btn
        v-if="isArchivedDraft && !isLockedToPublishedProfile && (hasEditAccess || checkRbacPermissions(['deleteDraftProfile']))"
        class="ghost-btn text-button-2 text-textDark"
        variant="text"
        rounded="lg"
        height="40"
        @click="showArchiveModal = true"
      >
        Unarchive
      </v-btn>
      <HideRbac :permissions="['createDraftProfile']">
        <v-btn
          v-if="canCreateDraftFromHris"
          class="ghost-btn text-button-2 text-textDark"
          variant="text"
          rounded="lg"
          height="40"
          :disabled="modalLoading"
          @click="setModal({ field: 'createDraftFromHris', value: true })"
        >
          Create Draft
        </v-btn>
      </HideRbac>
      <v-btn
        class="ghost-btn text-button-2 text-textDark"
        variant="text"
        rounded="lg"
        height="40"
      >
        <ExportModalProfileEditor
          :ulid="jobProfile?.ulid"
          :title="jobProfile?.title"
          :is-data-loaded="!rescorePending"
          @export-tracking-event="handleTrackingEvent($event)"
        />
      </v-btn>
      <template v-if="hasEditAccess || checkRbacPermissions(['saveDraftProfileRevision'])">
        <FirstDraftLanguageTooltip
          v-if="authData.aiEnabled && !isHrisProfile && !isTemplate"
          :show="isNonEnglish"
          :text="FIRST_DRAFT_PROFILE_TOOLTIP"
        >
          <FirstDraftButton
            :disabled="rescorePending || readOnly || isNonEnglish"
            variant="plain"
            class="first-draft-btn text-textBrand"
            height="40"
            @click="handleIterateWithFirstDraft"
          >
            <span class="text-button-2">
              {{ hasSummaryContent ? 'Iterate with First Draft' : 'Generate with First Draft' }}
            </span>
          </FirstDraftButton>
        </FirstDraftLanguageTooltip>
      </template>
    </div>

    <div class="w-25 d-flex justify-end">
      <HideRbac :permissions="['listDraftProfilesByProfileTemplate']">
        <TemplateUsageToggle
          v-if="isTemplate"
          class="pr-2 my-auto"
        />
      </HideRbac>
      <ApprovalsToggle class="pr-2 my-auto" />
      <HideRbac :permissions="['listFlsaSubmissions', 'listFlsaForms']">
        <FlsaToggle
          v-if="!(isStandaloneJobNav && isHrisProfile)"
          class="pr-2 my-auto"
        />
      </HideRbac>
      <HistoryToggle
        v-if="canShowHistoryToggle"
        class="pr-2 my-auto"
      />
      <CompareJobsToggle class="pr-2 my-auto" />
      <HideRbac :permissions="['cordComments']">
        <CommentsToggle
          v-if="!isHrisProfile"
          class="pr-2 my-auto"
        />
      </HideRbac>

      <AccessListToggle
        v-if="!isHrisProfile && !isTemplate"
        class="pr-2 my-auto"
      />

      <v-menu
        v-if="canDeleteTemplate || canArchiveDraft || checkRbacPermissions(['adminScoreBreakdown'])"
        location="bottom end"
        transition="scale-transition"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            class="my-auto mr-2"
            color="textDarkest"
            icon
            size="small"
            variant="text"
          >
            <v-icon icon="$dots-vertical" />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            v-if="canDeleteTemplate"
            class="text-body-2 text-textDarkest"
            @click="showDeleteModal = true"
          >
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canArchiveDraft"
            class="text-body-2 text-textDarkest"
            @click="showArchiveModal = true"
          >
            <v-list-item-title>Archive</v-list-item-title>
          </v-list-item>
          <HideRbac :permissions="['adminScoreBreakdown']">
            <v-list-item
              class="text-body-2 text-textDarkest"
              @click="openScoreBreakdown"
            >
              <v-list-item-title>Score Breakdown</v-list-item-title>
            </v-list-item>
          </HideRbac>
        </v-list>
      </v-menu>
    </div>
  </v-sheet>

  <Modal
    :show="showModal"
    cancel-text=""
    action-text=""
    :disabled="modalLoading"
    @close="showModal = false"
  >
    <template #title>
      Publish Template
    </template>
    <template #content>
      Publishing this template makes its current content available when creating new job profiles.
    </template>
    <template #actions>
      <DpplNewButton
        class="text-button-1"
        variant="flat"
        color="primary"
        width="150"
        height="40"
        :disabled="modalLoading"
        :loading="modalLoading"
        @click="handlePublish"
      >
        Publish
      </DpplNewButton>
    </template>
  </Modal>

  <Modal
    :show="showDraftPublishModal"
    cancel-text=""
    action-text=""
    :disabled="modalLoading"
    @close="showDraftPublishModal = false"
  >
    <template #title>
      Publish {{ jobProfile?.title || 'job profile' }}
    </template>
    <template #content>
      <p class="text-body-1 mb-0">
        Published jobs become visible to all users.
      </p>
    </template>
    <template #actions>
      <DpplNewButton
        class="text-button-1"
        variant="flat"
        color="primary"
        width="150"
        height="40"
        :disabled="modalLoading"
        :loading="modalLoading"
        @click="handleDraftPublish"
      >
        Publish
      </DpplNewButton>
    </template>
  </Modal>

  <ApplyJobProfileTemplateModal @applied="handleTemplateApplied" />

  <ArchiveModal
    :show="showArchiveModal"
    :name="jobProfile ? jobProfile.title : ''"
    :is-archived="isArchivedDraft"
    :loading="archiveLoading"
    @close="showArchiveModal = false"
    @confirm="handleConfirmArchive"
  />

  <DeleteModal
    :show="showDeleteModal"
    :name="jobProfile ? jobProfile.title : ''"
    document-type="template"
    detailed-text="template"
    @close="showDeleteModal = false"
    @delete="handleDeleteConfirm"
  />

  <Modal
    :show="show.createDraftFromHris"
    cancel-text="Cancel"
    action-text="Create Draft"
    :disabled="modalLoading"
    @close="setModal({ field: 'createDraftFromHris', value: false })"
    @action="handleCreateDraft"
  >
    <template #title>
      Create Draft
    </template>
    <template #content>
      A new draft job profile with the same content will be created.
    </template>
  </Modal>

  <Modal
    :show="showIterateModal"
    cancel-text="Cancel"
    :action-text="hasSummaryContent ? 'Iterate' : 'Generate'"
    @close="showIterateModal = false"
    @action="handleIterateConfirm"
  >
    <template #title>
      {{ hasSummaryContent ? 'Iterate with First Draft' : 'Generate with First Draft' }}
    </template>

    <template #content>
      <p>Generating will overwrite the current content of this job profile.</p>
      <v-checkbox
        v-if="hasSummaryContent"
        v-model="useExistingSummary"
        density="compact"
        hide-details
        class="mt-3"
      >
        <template #label>
          <span class="text-body-2 ml-2">
            Use existing summary content to generate new content
          </span>
        </template>
      </v-checkbox>
    </template>
  </Modal>

  <v-snackbar
    :model-value="show.jobProfileGenerated"
    location="top right"
    timeout="-1"
    close-on-content-click
    color="success"
    rounded="lg"
    content-class="pa-2"
    @click="setModal({ field: 'jobProfileGenerated', value: false })"
  >
    <div>
      Profile content generated successfully.
    </div>
    <div>
      Job description might be outdated. Consider updating it.
    </div>
    <div class="mt-4">
      <v-btn
        variant="outlined"
        size="small"
        rounded="lg"
        color="white"
        @click="setModal({ field: 'updateProfileJobDescription', value: true })"
      >
        Update Job Description
      </v-btn>
    </div>
  </v-snackbar>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useRouter } from 'vue-router';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useModals } from '@/stores/modals/modals';
import { useTracking } from '@/stores/tracking/tracking';
import Modal from '@/components/shared/Modal.vue';
import CommentsToggle from '@/components/document/rightPanel/CommentsToggle.vue';
import FlsaToggle from '@/components/document/rightPanel/FlsaToggle.vue';
import ApprovalsToggle from '@/components/document/rightPanel/ApprovalsToggle.vue';
import HistoryToggle from '@/components/document/rightPanel/HistoryToggle.vue';
import TemplateUsageToggle from '@/components/document/rightPanel/TemplateUsageToggle.vue';
import AccessListToggle from '@/components/document/rightPanel/AccessListToggle.vue';
import CompareJobsToggle from '@/components/document/rightPanel/CompareJobsToggle.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import FirstDraftLanguageTooltip from '@/components/firstDraft/FirstDraftLanguageTooltip.vue';
import { FIRST_DRAFT_PROFILE_TOOLTIP } from '@/helpers/language';
import ExportModalProfileEditor from '@/components/jobProfiles/editor/ExportModalProfileEditor.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import ArchiveModal from '@/components/shared/ArchiveModal.vue';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import ApplyJobProfileTemplateModal from '@/components/jobProfiles/editor/ApplyJobProfileTemplateModal.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { getJpEditorProfileType } from '@/helpers/jobProfileLibraryTracking';

const currentJobProfile = useCurrentJobProfile();
const {
  isTemplate, isHrisProfile, rescorePending, isAlreadyFinalized, jobProfile, summaryField, summaryFieldId, readOnly,
  hasEditAccess, isArchivedDraft, isNonEnglish,
} = storeToRefs(currentJobProfile);
const { archiveJobProfileByUlid, unarchiveJobProfileByUlid } = useJobProfileDrafts();

const { authData, isStandaloneJobNav } = storeToRefs(useAppAuthData());
const { checkRbacPermissions } = useAppAuthData();
const { addSuccessSnackbar } = useAppSnackbar();

const { route } = storeToRefs(useAppRoute());

const { modalLoading } = storeToRefs(useAppLoading());
const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const router = useRouter();

const showModal = ref(false);
const showDraftPublishModal = ref(false);
const showDeleteModal = ref(false);
const showArchiveModal = ref(false);
const archiveLoading = ref(false);
const showIterateModal = ref(false);
const useExistingSummary = ref(true);

const hasSummaryContent = computed(() => {
  if (!summaryField.value?.html) return false;
  return !isEmptyHtml(summaryField.value.html);
});

const publishDisabled = computed(() => (
  rescorePending.value || isAlreadyFinalized.value || readOnly.value
));

const draftPublishDisabled = computed(() => (
  rescorePending.value || isAlreadyFinalized.value || readOnly.value
));

const applyTemplateDisabled = computed(() => (
  rescorePending.value || readOnly.value
));

const canDeleteTemplate = computed(() => !isHrisProfile.value
  && isTemplate.value
  && checkRbacPermissions(['createProfileTemplate']));

// A connected draft follows its published profile, so standalone cannot archive it alone.
const isLockedToPublishedProfile = computed(() => isStandaloneJobNav.value
  && Boolean(jobProfile.value?.hris_reference_ulid));

// Hide the button once any draft is connected, even one this user cannot see.
const canCreateDraftFromHris = computed(() => isHrisProfile.value
  && !jobProfile.value?.has_connected_draft);

const canArchiveDraft = computed(() => !isTemplate.value
  && !isHrisProfile.value
  && !isArchivedDraft.value
  && !isLockedToPublishedProfile.value
  && (hasEditAccess.value || checkRbacPermissions(['deleteDraftProfile'])));

const canShowHistoryToggle = computed(() => {
  if (isHrisProfile.value) {
    return checkRbacPermissions(['listHrisProfileVersions']);
  }

  return hasEditAccess.value || checkRbacPermissions(['listDraftProfileRevisions']);
});

const openScoreBreakdown = () => {
  window.open(
    `/admin/job-profile/messages?ulid=${jobProfile.value?.ulid}&project_id=${authData.value.projectId}`,
    '_blank',
  );
};

const handleIterateWithFirstDraft = () => {
  useExistingSummary.value = true;
  showIterateModal.value = true;
};

const handleIterateConfirm = async () => {
  const keepsSummary = hasSummaryContent.value && useExistingSummary.value;

  useTracking().firstDraftIterateClick({
    documentType: 'profile',
    useExistingSummary: keepsSummary ? 'true' : 'false',
  });

  showIterateModal.value = false;

  await useFirstDraft().generateAndApplyJobProfile({
    ulid: jobProfile.value?.ulid,
    useExistingSummary: keepsSummary,
    summaryFieldId: summaryFieldId.value,
  });

  setModal({ field: 'jobProfileGenerated', value: true });
};

const handleCreateDraft = async () => {
  const sourceUlid = jobProfile.value?.ulid;
  const documentUlid = await currentJobProfile.createDraftFromHrisProfile();
  setModal({ field: 'createDraftFromHris', value: false });

  useTracking().jpEditorCreateDraftFromHris({
    ulid: sourceUlid,
    newUlid: documentUlid,
  });

  await router.push({ name: 'job-profile-view', params: { id: documentUlid } });
};

const handlePublish = async () => {
  await currentJobProfile.finalizeTemplate();
  showModal.value = false;

  useTracking().jpEditorPublish({
    ulid: jobProfile.value?.ulid,
    profileType: getJpEditorProfileType({
      isHris: isHrisProfile.value,
      isTemplate: isTemplate.value,
    }),
  });
};

const handleDraftPublish = async () => {
  const { jobProfileUlid } = await currentJobProfile.publishDraftProfile();

  showDraftPublishModal.value = false;

  useTracking().jpEditorPublishedDraft({
    ulid: jobProfile.value?.ulid,
    publishedUlid: jobProfileUlid,
  });

  addSuccessSnackbar({ text: 'Job profile published' });

  await router.push({ name: 'job-profile-hris-view', params: { id: jobProfileUlid } });
};

const handleTemplateApplied = async ({ documentUlid }) => {
  await currentJobProfile.loadJobProfile({ ulid: documentUlid });

  return router.replace({
    name: 'job-profile-view',
    params: { id: documentUlid },
    query: { ...(route.value?.query || {}) },
  });
};

const handleDeleteConfirm = async () => {
  const deletedUlid = jobProfile.value?.ulid;
  const deletedProfileType = getJpEditorProfileType({
    isHris: isHrisProfile.value,
    isTemplate: isTemplate.value,
  });

  showDeleteModal.value = false;
  await currentJobProfile.deleteJobProfile();

  useTracking().jpEditorDelete({
    ulid: deletedUlid,
    profileType: deletedProfileType,
  });

  await router.push({ name: 'job-profile-templates' });
};

const handleConfirmArchive = async () => {
  const ulid = jobProfile.value?.ulid;
  const wasArchived = isArchivedDraft.value;

  archiveLoading.value = true;

  try {
    const isDone = wasArchived
      ? await unarchiveJobProfileByUlid({ ulid })
      : await archiveJobProfileByUlid({ ulid });

    showArchiveModal.value = false;

    if (isDone) {
      await currentJobProfile.loadJobProfile({ ulid });

      if (wasArchived) {
        useTracking().jpEditorUnarchive({ ulid });
      } else {
        useTracking().jpEditorArchive({ ulid });
      }
    }
  } finally {
    archiveLoading.value = false;
  }
};

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};
</script>

<style scoped lang="less">
.publish-btn.v-btn {
  background: @bg-dark !important;
  color: @text-dark !important;

  &:not(.v-btn--disabled):hover {
    background: @bg-brand-light !important;
    color: @text-brand !important;
  }

  &.v-btn--disabled {
    opacity: 0.4;

    :deep(.v-btn__overlay) {
      display: none;
    }
  }
}

.ghost-btn.v-btn {
  padding: 0 6px;
}

.top-bar :deep(.first-draft-btn.first-draft-button) {
  padding: 0 6px;

  svg {
    width: 24px;
    height: 24px;
  }
}
</style>
